import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { fmtGNF, fmtDate, apiError, logError } from "../lib/api";
import { stockColorClass, moveColorClass } from "../lib/display";
import { ModalShell } from "../components/ModalShell";
import { useAuth } from "../context/AuthContext";
import { AmountInput } from "../components/AmountInput";

const MOVE_LABELS = { vente: "Vente", reception: "Réception", production: "Production", correction: "Correction", annulation_vente: "Annulation vente", stock_initial: "Stock initial", creation: "Création" };

function SupplierPicker({ supplier, setSupplier }) {
  const [mode, setMode] = useState("search");
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [fresh, setFresh] = useState({ name: "", phone: "" });

  useEffect(() => {
    if (mode !== "search" || search.length < 2) { setResults([]); return undefined; }
    const t = setTimeout(() => api.get(`/suppliers?search=${encodeURIComponent(search)}`).then((r) => setResults(r.data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [search, mode]);

  useEffect(() => {
    if (mode === "new") setSupplier(fresh.name && fresh.phone ? { name: fresh.name, phone: fresh.phone } : null);
  }, [fresh, mode, setSupplier]);

  if (supplier?.id) {
    return (
      <div className="flex items-center gap-2 bg-[#F6F4EF] rounded-xl px-3 py-2.5">
        <span className="text-sm font-semibold flex-1">{supplier.name} <span className="font-normal text-gray-500">· {supplier.phone}</span></span>
        <button type="button" data-testid="reception-supplier-clear-btn" className="text-xs text-[#B4432F]" onClick={() => setSupplier(null)}>Retirer</button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex gap-1.5">
        <button type="button" data-testid="supplier-mode-search" onClick={() => { setMode("search"); setSupplier(null); }}
          className={`text-xs px-3 py-1.5 rounded-full ${mode === "search" ? "bg-[#16233D] text-white" : "bg-white border border-[#E0DACE]"}`}>Fournisseur existant</button>
        <button type="button" data-testid="supplier-mode-new" onClick={() => { setMode("new"); setSupplier(null); }}
          className={`text-xs px-3 py-1.5 rounded-full ${mode === "new" ? "bg-[#16233D] text-white" : "bg-white border border-[#E0DACE]"}`}>Nouveau fournisseur</button>
      </div>
      {mode === "search" ? (
        <div className="relative">
          <MagnifyingGlass size={16} className="absolute left-3.5 top-3.5 text-gray-400" />
          <input data-testid="reception-supplier-search-input" className="kinput pl-9" placeholder="Rechercher par nom" value={search} onChange={(e) => setSearch(e.target.value)} />
          {results.length > 0 && (
            <div className="mt-1 w-full border border-[#E9E4DA] rounded-xl p-1 max-h-40 overflow-y-auto bg-white">
              {results.map((s) => (
                <button type="button" key={s.id} data-testid="reception-supplier-result"
                  onClick={() => { setSupplier({ id: s.id, name: s.name, phone: s.phone }); setSearch(""); setResults([]); }}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-[#F6F4EF] text-sm">
                  <span className="font-semibold">{s.name}</span> <span className="text-gray-500">· {s.phone}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <input data-testid="reception-new-supplier-name-input" className="kinput" placeholder="Nom du fournisseur" value={fresh.name} onChange={(e) => setFresh({ ...fresh, name: e.target.value })} />
          <input data-testid="reception-new-supplier-phone-input" className="kinput" placeholder="Téléphone" value={fresh.phone} onChange={(e) => setFresh({ ...fresh, phone: e.target.value })} />
        </div>
      )}
      <p className="text-xs text-gray-500">Le téléphone est affiché à côté du nom pour éviter toute confusion entre homonymes.</p>
    </div>
  );
}

function ReceptionForm({ product, onDone }) {
  const [rec, setRec] = useState({ qty: "", qty_unit: "unit", unit_cost: "", paid: true });
  const [supplier, setSupplier] = useState(null);
  const [busy, setBusy] = useState(false);
  const perCarton = product.units_per_carton;

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/products/${product.id}/reception`, {
        qty: parseFloat(rec.qty), qty_unit: rec.qty_unit, unit_cost: parseFloat(rec.unit_cost) || 0,
        supplier_id: supplier?.id || null, supplier_name: supplier?.name || null,
        supplier_phone: supplier?.phone || null, paid: rec.paid,
      });
      toast.success(rec.paid
        ? `Réception enregistrée — stock mis à jour et dépense « Achats/Marchandises » créée`
        : `Réception enregistrée — stock mis à jour et dette fournisseur créée`);
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const units = (parseFloat(rec.qty) || 0) * (rec.qty_unit === "carton" ? (perCarton || 1) : 1);
  const blocker = (() => {
    if (!(parseFloat(rec.qty) > 0)) return "Saisissez d'abord la quantité reçue.";
    if (!rec.paid && !(supplier?.name && supplier?.phone)) return "Pour une réception à régler plus tard, le fournisseur (nom + téléphone) est obligatoire.";
    return "";
  })();

  return (
    <div className="space-y-3" data-testid="reception-form">
      <h3 className="font-display font-bold">Réceptionner du stock <span className="font-normal text-sm text-gray-500">(s'ajoute à la quantité existante)</span></h3>
      {perCarton && (
        <div className="flex gap-1.5">
          {[["unit", "En unités"], ["carton", `En cartons (${perCarton} u.)`]].map(([v, l]) => (
            <button key={v} type="button" data-testid={`reception-unit-${v}`} onClick={() => setRec({ ...rec, qty_unit: v })}
              className={`text-xs px-3 py-1.5 rounded-full ${rec.qty_unit === v ? "bg-[#16233D] text-white" : "bg-white border border-[#E0DACE]"}`}>{l}</button>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <input data-testid="reception-qty-input" type="number" className="kinput" placeholder={rec.qty_unit === "carton" ? "Cartons reçus" : "Quantité reçue"} value={rec.qty} onChange={(e) => setRec({ ...rec, qty: e.target.value })} />
        <AmountInput data-testid="reception-cost-input" onOverflow={(m) => toast.error(m)} placeholder={rec.qty_unit === "carton" ? "Coût par carton (GNF)" : "Coût unitaire (GNF)"} value={rec.unit_cost} onChange={(v) => setRec({ ...rec, unit_cost: v })} />
      </div>
      {rec.qty_unit === "carton" && units > 0 && (
        <p data-testid="reception-conversion-hint" className="text-xs text-[#3B6AA0]">Soit {units.toLocaleString("fr-FR")} unités ajoutées au stock.</p>
      )}
      <div>
        <p className="text-sm font-semibold mb-2">Fournisseur</p>
        <SupplierPicker supplier={supplier} setSupplier={setSupplier} />
      </div>
      <div>
        <p className="text-sm font-semibold mb-2">Payé maintenant ou à régler plus tard ?</p>
        <div className="grid grid-cols-2 gap-2">
          <button type="button" data-testid="reception-paid-btn" onClick={() => setRec({ ...rec, paid: true })}
            className={`py-2.5 rounded-xl text-sm font-semibold border ${rec.paid ? "border-[#2E7D5B] bg-[#E3F0EA] text-[#2E7D5B]" : "border-[#E0DACE] bg-white"}`}>Payé maintenant</button>
          <button type="button" data-testid="reception-credit-btn" onClick={() => setRec({ ...rec, paid: false })}
            className={`py-2.5 rounded-xl text-sm font-semibold border ${!rec.paid ? "border-[#C99A2E] bg-[#F7EFD9] text-[#9a7620]" : "border-[#E0DACE] bg-white"}`}>À régler plus tard</button>
        </div>
      </div>
      <p className="text-xs text-gray-500">
        {rec.paid ? "Une dépense « Achats/Marchandises » sera créée automatiquement." : "Une dette fournisseur tracée sera créée (nom + téléphone requis)."}
      </p>
      {blocker && <p data-testid="reception-blocker" className="text-xs text-[#B4432F]">{blocker}</p>}
      <button data-testid="reception-submit-btn" className="btn-amber w-full py-3" disabled={!!blocker || busy} onClick={submit}>
        {busy ? "Enregistrement..." : "Enregistrer la réception"}
      </button>
    </div>
  );
}

function CorrectionForm({ product, onDone }) {
  const [corr, setCorr] = useState({ new_qty: "", reason: "" });

  const submit = async () => {
    try {
      await api.post(`/products/${product.id}/correction`, { new_qty: parseFloat(corr.new_qty), reason: corr.reason });
      toast.success("Correction tracée (ancienne/nouvelle valeur, auteur, motif)");
      onDone();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-3" data-testid="correction-form">
      <h3 className="font-display font-bold">Correction d'inventaire <span className="font-normal text-sm text-gray-500">(remplace la quantité — jamais un ajout)</span></h3>
      <input data-testid="correction-qty-input" type="number" className="kinput" placeholder={`Quantité constatée (actuel : ${product.stock_qty})`} value={corr.new_qty} onChange={(e) => setCorr({ ...corr, new_qty: e.target.value })} />
      <input data-testid="correction-reason-input" className="kinput" placeholder="Motif obligatoire (ex. inventaire physique)" value={corr.reason} onChange={(e) => setCorr({ ...corr, reason: e.target.value })} />
      <button data-testid="correction-submit-btn" className="btn-amber w-full py-3" disabled={corr.new_qty === "" || !corr.reason.trim()} onClick={submit}>Corriger le stock</button>
    </div>
  );
}

function ProductionForm({ product, onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ qty: "", date: today, note: "" });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/products/${product.id}/production`, {
        qty: parseFloat(form.qty), date: form.date, note: form.note,
      });
      toast.success(`Production enregistrée : +${parseFloat(form.qty)} pour ${product.name}. Nouveau stock : ${data.new_qty}.`);
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="production-form">
      <h3 className="font-display font-bold">Entrée de production <span className="font-normal text-sm text-gray-500">(s'ajoute au stock existant)</span></h3>
      <input data-testid="production-qty-input" type="number" className="kinput" placeholder="Quantité produite" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} />
      <div>
        <label className="text-xs text-gray-500">Date de production</label>
        <input data-testid="production-date-input" type="date" max={today} className="kinput" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
      </div>
      <input data-testid="production-note-input" className="kinput" placeholder="Note (optionnel)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
      <p className="text-xs text-gray-500">Aucun fournisseur, aucun montant : cette entrée ne crée ni dépense ni dette.</p>
      <button data-testid="production-submit-btn" className="btn-amber w-full py-3" disabled={!(parseFloat(form.qty) > 0) || busy} onClick={submit}>
        {busy ? "Enregistrement..." : "Enregistrer la production"}
      </button>
    </div>
  );
}

function PriceEditor({ product, onDone, isPatron }) {
  const [prices, setPrices] = useState({ purchase_price: product.purchase_price ?? "", sale_price: product.sale_price });

  const save = async () => {
    try {
      const body = { sale_price: parseFloat(prices.sale_price) || 0 };
      // Un non-patron ne voit pas le prix d'achat : il ne doit jamais l'écraser.
      if (isPatron) body.purchase_price = parseFloat(prices.purchase_price) || 0;
      await api.patch(`/products/${product.id}`, body);
      toast.success("Prix mis à jour — aucune dépense créée (donnée de référence uniquement)");
      onDone();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-3" data-testid="price-form">
      <h3 className="font-display font-bold">Modifier les prix</h3>
      <div className={`grid gap-2 ${isPatron ? "grid-cols-2" : "grid-cols-1"}`}>
        {isPatron && (
          <input data-testid="edit-purchase-price-input" type="number" className="kinput" placeholder="Prix d'achat"
            value={prices.purchase_price} onChange={(e) => setPrices({ ...prices, purchase_price: e.target.value })} />
        )}
        <input data-testid="edit-sale-price-input" type="number" className="kinput" placeholder="Prix de vente"
          value={prices.sale_price} onChange={(e) => setPrices({ ...prices, sale_price: e.target.value })} />
      </div>
      {isPatron && <p className="text-xs text-gray-500">Le prix d'achat sert uniquement au calcul de la marge. Le modifier ne crée jamais de dépense.</p>}
      <button data-testid="price-submit-btn" className="btn-amber w-full py-3" onClick={save}>Enregistrer</button>
    </div>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can, user } = useAuth();
  const noStock = user?.company?.stock_mode === "no_stock";
  const [p, setP] = useState(null);
  const [tab, setTab] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = useCallback(() => api.get(`/products/${id}`).then((r) => setP(r.data)).catch((e) => logError(e)), [id]);
  useEffect(() => { load(); }, [load]);

  const done = () => { setTab(null); load(); };

  const archive = async () => {
    try {
      await api.post(`/products/${id}/archive`);
      toast.success("Produit archivé — retiré de la vente et des alertes, historique conservé");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const restore = async () => {
    try {
      await api.post(`/products/${id}/restore`);
      toast.success("Produit restauré"); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const destroy = async () => {
    try {
      await api.delete(`/products/${id}`);
      toast.success("Produit supprimé définitivement — action tracée");
      navigate("/stock");
    } catch (e) { toast.error(apiError(e)); setConfirmDelete(false); }
  };

  if (!p) return <p className="text-sm text-gray-400 py-10">Chargement...</p>;

  const tabBtn = (key, label, testid) => (
    <button data-testid={testid} onClick={() => setTab(tab === key ? null : key)}
      className={`py-2.5 px-3 rounded-xl font-display font-semibold text-sm ${tab === key ? "bg-[#16233D] text-white" : "btn-ghost"}`}>{label}</button>
  );

  return (
    <div className="max-w-2xl space-y-4 fade-in" data-testid="product-detail-page">
      <button onClick={() => navigate("/stock")} className="flex items-center gap-1 text-sm text-gray-500"><ArrowLeft size={16} /> Stock</button>
      <div className="kcard p-5">
        <h1 className="font-display font-bold text-2xl mb-1">{p.name}</h1>
        <p className="text-sm text-gray-500 mb-4">
          {p.category || "Sans catégorie"}
          {p.units_per_carton ? ` · carton de ${p.units_per_carton} unités` : ""}
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
          <div><p className="text-xs text-gray-500">Stock actuel</p><p className={`font-display font-bold text-xl tabular-nums ${stockColorClass(p.stock_qty, p.alert_threshold)}`}>{p.stock_qty}</p></div>
          <div><p className="text-xs text-gray-500">Seuil d'alerte</p><p className="font-display font-bold text-xl tabular-nums">{p.alert_threshold}</p></div>
          {user?.role === "patron" && (
            <div><p className="text-xs text-gray-500">Prix d'achat</p><p className="font-display font-bold text-sm tabular-nums mt-1.5">{fmtGNF(p.purchase_price)}</p></div>
          )}
          <div><p className="text-xs text-gray-500">Prix de vente</p><p className="font-display font-bold text-sm tabular-nums mt-1.5">{fmtGNF(p.sale_price)}</p></div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-5">
          {can("stock_receive") && tabBtn("rec", "+ Réceptionner du stock", "reception-tab-btn")}
          {can("stock_production") && !noStock && tabBtn("prod", "+ Entrée de production", "production-tab-btn")}
          {can("stock_correct") && tabBtn("corr", "Correction d'inventaire", "correction-tab-btn")}
          {can("products_price") && tabBtn("price", "Modifier les prix", "price-tab-btn")}
        </div>
        {user?.role === "patron" && (
          <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-[#EFE9DE]">
            {p.archived ? (
              <button data-testid="product-restore-btn" className="btn-ghost text-xs py-2 px-3" onClick={restore}>Restaurer</button>
            ) : (
              <button data-testid="product-archive-btn" className="btn-ghost text-xs py-2 px-3" onClick={archive}>Archiver</button>
            )}
            <button data-testid="product-delete-btn" className="btn-ghost text-xs py-2 px-3 text-[#B4432F]" onClick={() => setConfirmDelete(true)}>Supprimer définitivement</button>
          </div>
        )}
      </div>

      {tab && (
        <ModalShell size="max-w-lg" testid="product-action-modal" onClose={() => setTab(null)}>
          {tab === "rec" && <ReceptionForm product={p} onDone={done} />}
          {tab === "prod" && <ProductionForm product={p} onDone={done} />}
          {tab === "corr" && <CorrectionForm product={p} onDone={done} />}
          {tab === "price" && <PriceEditor product={p} onDone={done} isPatron={user?.role === "patron"} />}
          <button data-testid="product-action-close" onClick={() => setTab(null)} className="btn-ghost w-full py-2.5 text-sm">Fermer</button>
        </ModalShell>
      )}

      {confirmDelete && (
        <ModalShell size="max-w-sm" testid="product-delete-modal" onClose={() => setConfirmDelete(false)}>
          <h3 className="font-display font-bold text-lg">Supprimer « {p.name} » ?</h3>
          <p className="text-sm text-gray-600">
            La suppression définitive n'est possible que si le produit n'a jamais eu de vente, de réception,
            d'entrée de production ni de correction. Sinon, archivez-le : son historique sera conservé.
          </p>
          <div className="flex gap-2">
            <button className="btn-ghost flex-1 py-2.5" onClick={() => setConfirmDelete(false)}>Annuler</button>
            <button data-testid="product-delete-confirm" className="btn-amber flex-1 py-2.5" onClick={destroy}>Supprimer</button>
          </div>
        </ModalShell>
      )}

      <div className="kcard p-5">
        <h3 className="font-display font-bold mb-3">Historique des mouvements</h3>        <div className="space-y-1.5 max-h-96 overflow-y-auto">
          {p.movements.map((m) => (
            <div key={m.id} data-testid="movement-row" className="flex items-center gap-3 px-3 py-2 rounded-xl bg-[#F6F4EF]/60 text-sm">
              <span className={`font-semibold shrink-0 w-32 ${moveColorClass(m.type)}`}>{MOVE_LABELS[m.type] || m.type}</span>
              <span className="tabular-nums font-semibold">{m.qty_change > 0 ? "+" : ""}{m.qty_change}</span>
              <span className="text-xs text-gray-500">{m.old_qty} → {m.new_qty}</span>
              <span className="flex-1" />
              <span className="text-xs text-gray-400 text-right">{m.author_name}<br />{fmtDate(m.created_at)}</span>
            </div>
          ))}
          {p.movements.length === 0 && <p className="text-sm text-gray-400">Aucun mouvement.</p>}
        </div>
      </div>
    </div>
  );
}
